import { MapsWidget } from '@/components/MapsWidget';
export const frontmatter = {
  category: 'atracao',
  title: 'Farol das Conchas: Como Subir e a Vista 360° da Ilha do Mel',
  excerpt:
    'Guia completo para subir no Farol das Conchas na Ilha do Mel. Trilha, horários, preço da entrada e a melhor hora para ver o pôr do sol do topo da ilha.',
  publishedAt: '2026-04-24',
  author: 'Guia Ilha do Mel',
  tags: ['farol', 'trilha', 'passeio', 'vista-panoramica'],
  coverImage: '/images/praia-do-farol-hero.jpg',
  readingTime: '6 min',
};

# Farol das Conchas: Como Subir e a Vista 360°

O **Farol das Conchas** é o cartão-postal mais icônico da [Ilha do Mel](/). Localizado no topo do Morro do Farol, oferece uma das vistas mais espetaculares do litoral paranaense — mar, mata atlântica e vilarejos de um único ponto.

Neste guia, explicamos **como chegar**, **quanto custa**, **o que esperar** da subida e a **melhor hora** para estar lá em cima.

---

## Onde fica o Farol das Conchas?

O farol fica no **Morro do Farol**, no centro da ilha, entre as praias do Farol e da Praia Grande. É acessível por uma trilha sinalizada que parte da base do morro.

| Informação          | Detalhe                          |
| ------------------- | -------------------------------- |
| **Localização**     | Morro do Farol, Ilha do Mel — PR |
| **Altitude**        | 150 metros acima do nível do mar |
| **Acesso**          | Trilha de terra e pedras         |
| **Tempo de subida** | 15-20 minutos (ritmo leve)       |
| **Dificuldade**     | Leve a moderada                  |

---

## Como chegar ao pé da trilha

### Partindo de Nova Brasília

1. Siga pela trilha principal sentido sul (em direção às Encantadas)
2. Após ~10 min de caminhada, haverá uma placa indicando "Farol" à esquerda
3. A trilha do farol sobe pela encosta do morro

### Partindo das Encantadas

1. Siga pela trilha principal sentido norte (em direção a Nova Brasília)
2. A placa do farol estará à direita após passar pela Praia do Farol
3. Distância total: ~25-30 min de caminhada desde o trapiche das Encantadas

---

## A trilha: o que esperar

A subida é **curta mas íngreme em alguns trechos**. Não exige equipamento especial, mas alguns cuidados são importantes:

- **Calçado:** use tênis com borracha de tração. As pedras podem estar escorregadias, especialmente após chuva.
- **Água:** leve uma garrafa pequena. Não há ponto de venda no topo.
- **Mosquito:** repelente ajuda nos trechos de mata fechada.
- **Guarda-chuva ou capa de chuva:** o tempo muda rápido na ilha.

> **Dica de morador:** suba no final da tarde (1h antes do pôr do sol). A luz dourada sobre o mar é cinematográfica — e a temperatura mais amena torna a subida mais confortável.

---

## No topo: a vista 360°

Ao chegar ao topo, você encontrará:

- O **farol histórico** (construído em 1872, ainda operacional)
- Uma **área de observação** cercada por grades de segurança
- Placas informativas sobre a história do farol e da ilha

### O que dá para ver de cada lado:

| Direção   | Vista                                              |
| --------- | -------------------------------------------------- |
| **Leste** | Oceano Atlântico e o horizonte sem fim             |
| **Sul**   | Praia de Encantadas e o continente ao fundo        |
| **Oeste** | Baía de Paranaguá, manguezais e Serra do Mar       |
| **Norte** | Praia Grande, Nova Brasília e as praias do litoral |

Em dias claros, é possível avistar o **Porto de Paranaguá** e até a cidade de Paranaguá ao longe.

---

## Horários e preço

| Item                         | Detalhe                                |
| ---------------------------- | -------------------------------------- |
| **Horário de funcionamento** | 9h às 17h (todos os dias)              |
| **Entrada**                  | Gratuita                               |
| **Melhor horário**           | 16h30-17h (pôr do sol)                 |
| **Restrições**               | Proibido subir nas estruturas do farol |

> **Atenção:** o farol fecha ao anoitecer por segurança. Não fique no topo após o horário de fechamento — a descida no escuro é perigosa.

---

## Fotos: como capturar a vista

- **Lente grande-angular** (16-24mm) captura toda a extensão da vista
- **Horário dourado** (1h antes do pôr do sol) oferece luz quente e sombras longas
- **Tripé leve** útil para longa exposição do mar ao entardecer
- **Drone:** voar no topo é permitido, mas atenção ao vento forte

---

## Regras de visitação

- Não deixe lixo na trilha ou no topo
- Não arranhe ou pichace as estruturas do farol
- Respeite o silêncio — é um local de contemplação
- Animais de estimação são permitidos na trilha, mas devem estar de coleira

---

## Combine com outros passeios

O Farol das Conchas fica a meio caminho entre os dois vilarejos. Combine a visita com:

- **Praia do Farol** — praia logo abaixo do morro, perfeita para descansar após a subida
- **Trilha das Encantadas** — continuação da trilha principal até o vilarejo sul
- **Praia Grande** — a maior extensão de areia da ilha, a 10 min de caminhada

O farol também foi **cenário do filme "A Ostra e o Vento" (1997)**, com Leandra Leal e Lima Duarte — a primeira produção brasileira rodada na ilha. Saiba mais sobre [a história do Farol das Conchas no cinema](/blog/ilha-do-mel-no-cinema).

## <MapsWidget query="Farol das Conchas Ilha do Mel" />

Quer planejar o roteiro completo da Ilha do Mel? Nosso guia completo da [Ilha do Mel](/) tem trilhas, praias, restaurantes e dicas de moradores para você aproveitar cada canto da ilha.
